<script setup lang="ts">
import { CircleQuestionMark, MailQuestionMark, Moon, Sun } from 'lucide-vue-next';
import { type Component, computed, onMounted, provide, ref, shallowRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import AccountActivationRequired from '@components/login/account-activation-required.vue';
import Error from '@components/login/error.vue';
import ForgottenPasswordForm from '@components/login/form/forgotten-password.vue';
import LoginForm from '@components/login/form/login.vue';
import PasswordReset from '@components/login/form/password-reset.vue';
import SignUpForm from '@components/login/form/sign-up.vue';
import SingleSignOnMultiForm from '@components/login/form/single-sign-on-multi.vue';
import PasswordResetTokenExpired from '@components/login/password-reset-token-expired.vue';
import SingleSignOnNoAccount from '@components/login/single-sign-on-no-account.vue';
import TwoFactorAuthenticationResetError from '@components/login/two-factor-reset-error.vue';
import TwoFactorAuthenticationResetSuccess from '@components/login/two-factor-reset-success.vue';
import Logo from '@components/logo.vue';
import PrivacyAndTerms from '@components/privacy-and-terms.vue';
import loginResponseHandler from '@library/login-response-handler';
import DarkMode from '@modules/dark-mode';
import { type RouteDefinition, type RouteMatch, type RouteParams, RoutePathsLogin, Router } from '@modules/router';
import setPageTitle from '@modules/set-page-title';
import { type SSOServer } from '@types';

const { t } = useI18n();

const props = defineProps<{
    countryList: Record<string, string>;
    enterpriseCompanyName: string;
    enterpriseLink: string;
    enterpriseLogo: string;
    isEnterprise: boolean;
    isMultiSSO: boolean;
    ssoServers: Array<SSOServer>;
    supportEmailAddress: string;
    websiteSupport: string;
    websiteMarketing: string;
}>();

const currentView = shallowRef<Component>(props.isMultiSSO ? SingleSignOnMultiForm : LoginForm);
const isSignUp = computed(() => currentView.value === SignUpForm);
const currentViewTitle = ref<string>(props.isMultiSSO ? t('singleSignOn') : t('login'));
const currentRouteParams = ref<RouteParams>({});

const darkMode = new DarkMode();
const isDarkMode = ref(darkMode.isDarkMode());
watch(isDarkMode, (value) => (value ? darkMode.enable() : darkMode.disable()));
if (isDarkMode.value) darkMode.enable();

const routes: Array<RouteDefinition> = [
    {
        component: SingleSignOnNoAccount,
        path: RoutePathsLogin.SINGLE_SIGN_ON_NO_ACCOUNT,
        title: t('singleSignOn')
    }
];

if (!props.isMultiSSO) {
    routes.push(
        ...[
            {
                component: Error,
                path: RoutePathsLogin.ACCOUNT_ACTIVATION_FAILED,
                title: t('accountActivationFailed')
            },
            {
                component: AccountActivationRequired,
                path: RoutePathsLogin.ACCOUNT_ACTIVATION_REQUIRED,
                title: t('accountActivationRequired')
            },
            {
                component: Error,
                path: RoutePathsLogin.ACCOUNT_NOT_FOUND,
                title: t('accountNotFound')
            },
            {
                component: ForgottenPasswordForm,
                path: RoutePathsLogin.FORGOTTEN_PASSWORD,
                title: t('forgottenPassword')
            },
            {
                component: LoginForm,
                path: RoutePathsLogin.LOGIN,
                title: t('login')
            },
            {
                component: PasswordResetTokenExpired,
                path: RoutePathsLogin.PASSWORD_RESET_LINK_EXPIRED,
                title: t('passwordReset')
            },
            {
                component: PasswordReset,
                path: RoutePathsLogin.PASSWORD_RESET_TOKEN,
                title: t('passwordReset')
            },
            {
                component: TwoFactorAuthenticationResetError,
                path: RoutePathsLogin.TWO_FACTOR_RESET_ERROR,
                title: t('twoFactorAuthenticationReset')
            },
            {
                component: TwoFactorAuthenticationResetSuccess,
                path: RoutePathsLogin.TWO_FACTOR_RESET_SUCCESS,
                title: t('twoFactorAuthenticationReset')
            }
        ]
    );

    if (!props.isEnterprise) {
        routes.push({
            component: SignUpForm,
            path: RoutePathsLogin.SIGN_UP,
            title: t('signUp')
        });
    }
} else {
    routes.push({
        component: SingleSignOnMultiForm,
        path: RoutePathsLogin.SINGLE_SIGN_ON,
        title: t('singleSignOn')
    });
}

const router = new Router(routes);

const updateFromHash = () => {
    const match: RouteMatch = router.matchRoute(window.location.hash.slice(1) || '/');

    if (match) {
        currentView.value = match.component;
        currentViewTitle.value = match.title;
        currentRouteParams.value = match.params;
        setPageTitle(match.title, t);
        return;
    }

    currentView.value = props.isMultiSSO ? SingleSignOnMultiForm : LoginForm;
    currentViewTitle.value = props.isMultiSSO ? t('singleSignOn') : t('login');
    currentRouteParams.value = {};
    setPageTitle(props.isMultiSSO ? t('singleSignOn') : t('login'), t);
};
window.addEventListener('hashchange', updateFromHash);

provide('setPageTitle', (title: string) => {
    currentViewTitle.value = title;
});

onMounted(() => updateFromHash());
</script>

<template>
    <div class="flex flex-col h-full w-full items-center justify-start md:justify-center p-2">
        <div class="w-full md:w-[90%] max-w-[1280px]">
            <div class="w-full flex flex-col items-center lg:flex-row gap-4 lg:gap-8">
                <div class="flex flex-col gap-4 justify-center w-full lg:w-[50%] md:h-full">
                    <div class="md:max-w-[400px] px-10 pt-10 pb-6 lg:p-0">
                        <a
                            :aria-label="$t('ariaLabel.xerScheduleToolkitWebsite')"
                            :href="props.websiteMarketing"
                            target="_blank">
                            <Logo />
                        </a>
                    </div>
                    <div v-if="!isSignUp" class="hidden lg:inline-block font-secondary text-md md:text-lg">
                        <p>
                            {{ $t('taglineBranding') }}
                        </p>
                        <p class="font-bold">
                            {{ $t('taglineBranding2') }}
                        </p>
                    </div>
                    <div v-else class="hidden lg:inline-block font-secondary text-md md:text-lg">
                        <p class="font-bold">
                            {{ $t('taglineSignUp') }}
                        </p>
                        <p>
                            {{ $t('taglineSignUp2') }}
                        </p>
                    </div>
                </div>
                <div class="w-full lg:w-[50%] md:h-full m-2 md:m-0">
                    <div class="bg-content-background border rounded-xl border-surface p-5">
                        <p v-if="currentViewTitle" class="font-secondary text-2xl">
                            {{ currentViewTitle }}
                        </p>
                        <component
                            v-bind="currentRouteParams"
                            class="mt-4"
                            :is="currentView"
                            :countryList="props.countryList"
                            :loginResponseHandler="loginResponseHandler"
                            :servers="props.ssoServers"
                            :showSignUp="!props.isEnterprise" />
                    </div>
                </div>
            </div>
            <div class="w-full flex flex-col md:flex-row justify-end mt-2">
                <Button
                    variant="text"
                    v-tooltip.left="isDarkMode ? $t('switchToLightMode') : $t('switchToDarkMode')"
                    severity="secondary"
                    size="small"
                    @click="isDarkMode = !isDarkMode">
                    <Sun v-if="!isDarkMode" />
                    <Moon v-if="isDarkMode" />
                </Button>
                <div
                    class="flex flex-col md:flex-row gap-4 items-center justify-between mt-2 md:mt-0 md:bg-content-background md:border-t md:border-surface md:mb-0 md:absolute md:right-0 md:left-0 md:bottom-0 md:p-4">
                    <div class="w-full md:w-auto flex flex-col-reverse md:flex-row items-center justify-between">
                        <PrivacyAndTerms v-if="!props.isEnterprise" :supportEmailAddress="props.supportEmailAddress" />
                        <Button
                            :aria-label="$t('supportCentre')"
                            as="a"
                            class="w-full md:w-auto"
                            :href="props.websiteSupport"
                            :label="$t('supportCentre')"
                            severity="secondary"
                            size="small"
                            target="_blank"
                            variant="text">
                            <template #icon>
                                <CircleQuestionMark :size="16" />
                            </template>
                        </Button>
                        <Button
                            v-if="!!props.supportEmailAddress"
                            as="a"
                            class="w-full md:w-auto"
                            :href="
                                'mailto:' +
                                props.supportEmailAddress +
                                '?Subject=' +
                                encodeURI($t('xerScheduleToolkit') + ' - ' + $t('supportRequest'))
                            "
                            :label="props.supportEmailAddress"
                            severity="secondary"
                            size="small"
                            variant="text">
                            <template #icon>
                                <MailQuestionMark :size="16" />
                            </template>
                        </Button>
                    </div>
                    <div
                        v-if="props.isEnterprise"
                        class="flex flex-col md:flex-row gap-4 items-center justify-end mt-2 md:mt-0">
                        <span v-if="!!props.enterpriseCompanyName" class="text-surface-500">
                            {{ $t('powering') }}
                            <strong>{{ props.enterpriseCompanyName }}</strong>
                        </span>
                        <div
                            v-if="!!props.enterpriseLogo"
                            class="bg-surface-0 rounded-md p-4 max-w-[150px] mb-4 md:mb-0">
                            <a :href="props.enterpriseLink" target="_blank">
                                <img :alt="$t('enterpriseLogo')" :src="props.enterpriseLogo" />
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
